/*
 * Schuh Galaxy shell behaviours: mega menu, header style below 1280px, live free-shipping messages.
 * Loaded once from layout/theme.liquid. Uses delegation and custom elements, so it keeps working
 * after the theme editor re-renders the header or the mega-menu section.
 */

const DESKTOP = window.matchMedia('(min-width: 1280px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ------------------------------------------------------------------ */
/* Header: hamburger below 1280px (design "SG Header")                 */
/* ------------------------------------------------------------------ */

function syncHeaderMenuStyle() {
  const header = document.querySelector('#header-component');
  if (!header) return;
  if (!DESKTOP.matches) {
    header.dataset.menuStyle = 'drawer';
    return;
  }
  const overflow = header.querySelector('overflow-list');
  const touch = window.matchMedia('(hover: none)').matches;
  header.dataset.menuStyle = touch || overflow?.hasAttribute('minimum-reached') ? 'drawer' : 'menu';
}

DESKTOP.addEventListener('change', syncHeaderMenuStyle);
document.addEventListener('shopify:section:load', () => requestAnimationFrame(syncHeaderMenuStyle));

/* ------------------------------------------------------------------ */
/* Mega menu                                                           */
/* ------------------------------------------------------------------ */

class NovoMegaMenu extends HTMLElement {
  #closeTimer = 0;
  /** @type {HTMLElement | null} */
  #trigger = null;

  connectedCallback() {
    document.addEventListener('pointerover', this.#onPointerOver);
    document.addEventListener('focusin', this.#onFocusIn);
    document.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('pointerleave', this.#scheduleClose);
    this.addEventListener('pointerenter', this.#cancelClose);
    window.addEventListener('scroll', this.#position, { passive: true });
    DESKTOP.addEventListener('change', this.#onBreakpoint);
    document.addEventListener('shopify:block:select', this.#onEditorSelect);
    document.addEventListener('shopify:block:deselect', this.#onEditorDeselect);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerover', this.#onPointerOver);
    document.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('scroll', this.#position);
    DESKTOP.removeEventListener('change', this.#onBreakpoint);
    document.removeEventListener('shopify:block:select', this.#onEditorSelect);
    document.removeEventListener('shopify:block:deselect', this.#onEditorDeselect);
    this.#setExpanded(false);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /** The first N top-level links of the Horizon header menu open the panel. */
  #triggerItems() {
    const count = Number(this.dataset.triggerCount || 0);
    const items = [...document.querySelectorAll('#header-component .menu-list__list-item:not([slot="more"])')].filter(
      (item) => !item.closest('.menu-list--mobile, header-drawer')
    );
    return items.slice(0, count);
  }

  /** @param {EventTarget | null} target */
  #triggerFor(target) {
    if (!(target instanceof Element)) return null;
    const item = target.closest('.menu-list__list-item');
    if (!item || !this.#triggerItems().includes(/** @type {HTMLElement} */ (item))) return null;
    return /** @type {HTMLElement | null} */ (item.querySelector('.menu-list__link'));
  }

  #onPointerOver = (event) => {
    if (!DESKTOP.matches) return;
    const link = this.#triggerFor(event.target);
    if (link) {
      this.open(link);
      return;
    }
    const target = event.target instanceof Element ? event.target : null;
    if (!this.isOpen || !target) return;
    // Leaving the trigger zone: anything in the header row that is not a trigger, or outside header and panel.
    if (this.contains(target)) return this.#cancelClose();
    this.#scheduleClose();
  };

  #onFocusIn = (event) => {
    if (!DESKTOP.matches) return;
    const link = this.#triggerFor(event.target);
    if (link) return this.open(link);
    if (this.isOpen && event.target instanceof Node && !this.contains(event.target)) this.close();
  };

  #onKeyDown = (event) => {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' && this.#triggerFor(event.target)) {
        event.preventDefault();
        this.open(/** @type {HTMLElement} */ (this.#triggerFor(event.target)));
        this.querySelector('a')?.focus();
      }
      return;
    }
    if (event.key === 'Escape') {
      const trigger = this.#trigger;
      this.close();
      trigger?.focus();
    } else if (event.key === 'ArrowDown' && this.#triggerFor(event.target)) {
      event.preventDefault();
      this.querySelector('a')?.focus();
    }
  };

  #onBreakpoint = () => {
    if (!DESKTOP.matches) this.close();
  };

  #onEditorSelect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) this.open(this.#triggerItems()[0]?.querySelector('a') ?? null);
  };

  #onEditorDeselect = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) this.close();
  };

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
  };

  #scheduleClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(), 160);
  };

  #position = () => {
    const header = document.querySelector('#header-component');
    if (!header) return;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    this.style.setProperty('--novo-mega-top', `${bottom}px`);
  };

  /** @param {HTMLElement | null} link */
  #setExpanded(open, link = this.#trigger) {
    for (const item of this.#triggerItems()) {
      item.querySelector('.menu-list__link')?.setAttribute('aria-expanded', 'false');
    }
    if (open && link) link.setAttribute('aria-expanded', 'true');
  }

  /** @param {HTMLElement | null} link */
  open(link) {
    this.#cancelClose();
    this.#trigger = link;
    this.#position();
    this.#setExpanded(true, link);
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.#cancelClose();
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    this.#setExpanded(false);
    this.#trigger = null;
  }
}

if (!customElements.get('novo-mega-menu')) {
  customElements.define('novo-mega-menu', NovoMegaMenu);
}

/* ------------------------------------------------------------------ */
/* Free shipping: announcement bar + drawer ring, live after cart changes */
/* ------------------------------------------------------------------ */

/**
 * @param {number} cents
 * @param {string} format Shopify money format, e.g. "£{{amount}}"
 */
function formatMoney(cents, format) {
  const value = (cents / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_with_comma_separator':
        return `${dots},${dec}`;
      case 'amount_no_decimals_with_comma_separator':
        return dots;
      case 'amount_no_decimals':
        return commas;
      default:
        return `${commas}.${dec}`;
    }
  });
}

const moneyFormat = () =>
  document.querySelector('[data-money-format]')?.getAttribute('data-money-format') || '£{{amount}}';

async function refreshShippingMessages() {
  const targets = document.querySelectorAll('[data-novo-ship-bar]');
  if (!targets.length) return;
  let cart;
  try {
    cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })).json();
  } catch (error) {
    return;
  }
  for (const el of targets) {
    const threshold = Number(el.getAttribute('data-threshold') || 0);
    const remaining = threshold > 0 ? Math.max(0, threshold - cart.total_price) : 0;
    const html =
      remaining > 0
        ? (el.getAttribute('data-msg-away') || '').replace('[amount]', formatMoney(remaining, moneyFormat()))
        : el.getAttribute('data-msg-free') || '';
    if (el.innerHTML.trim() !== html) el.innerHTML = html;
  }
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  const promise = /** @type {any} */ (event).promise;
  Promise.resolve(promise).finally(() => window.setTimeout(refreshShippingMessages, 50));
});

/* ------------------------------------------------------------------ */
/* Cart drawer: soft fade-in of the empty state                        */
/* ------------------------------------------------------------------ */

new MutationObserver((records) => {
  if (REDUCED.matches) return;
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      const empty = node.matches('.novo-cart-empty') ? node : node.querySelector?.('.novo-cart-empty');
      if (!empty || empty.classList.contains('is-in')) continue;
      empty.classList.add('novo-fx-enter');
      requestAnimationFrame(() => requestAnimationFrame(() => empty.classList.add('is-in')));
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

/* ------------------------------------------------------------------ */
/* Cart: busy from the first click (Softstead). Horizon waits 300 ms   */
/* (debounce) before it marks the cart as updating, so the + / − click  */
/* showed nothing for that long. .novo-cart-pending bridges the gap.    */
/* ------------------------------------------------------------------ */

document.addEventListener('quantity-selector:update', (event) => {
  const items = event.target instanceof Element ? event.target.closest('cart-items-component') : null;
  if (!items || items.classList.contains('novo-cart-pending')) return;
  items.classList.add('novo-cart-pending');
  const clear = () => {
    items.classList.remove('novo-cart-pending');
    observer.disconnect();
    clearTimeout(fallback);
  };
  // Horizon's own busy state takes over and then ends; when it ends, ours goes too.
  let seenBusy = false;
  const observer = new MutationObserver(() => {
    if (items.classList.contains('cart-items-disabled')) seenBusy = true;
    else if (seenBusy) clear();
  });
  observer.observe(items, { attributes: true, attributeFilter: ['class'] });
  const fallback = setTimeout(clear, 6000);
});

/* ------------------------------------------------------------------ */
/* "Check out": spinner until the checkout page takes over              */
/* ------------------------------------------------------------------ */

document.addEventListener('submit', (event) => {
  const submitter = /** @type {SubmitEvent} */ (event).submitter;
  if (!(submitter instanceof HTMLElement) || !submitter.matches('.cart__checkout-button')) return;
  if (submitter.classList.contains('is-loading')) return;
  submitter.classList.add('is-loading');
  submitter.setAttribute('aria-busy', 'true');
  submitter.insertAdjacentHTML('beforeend', '<span class="novo-busy novo-busy--inverse" aria-hidden="true"></span>');
});

/* ------------------------------------------------------------------ */
/* Contact form and newsletter (Softstead): ring on the submit button   */
/* until the page comes back with the result.                          */
/* ------------------------------------------------------------------ */

/** @param {Element} button */
function novoClearBusy(button) {
  button.classList.remove('is-loading', 'novo-form-busy');
  button.removeAttribute('aria-busy');
  button.querySelector(':scope > .novo-busy')?.remove();
}

// Capture phase: Shopify's form bot protection cancels the submit event and re-submits the form itself,
// so a bubbling listener would never see it.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('#NovoContactForm, #NovoFooterNewsletter, .contact-form__form')) return;
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    const button = submitter instanceof HTMLElement ? submitter : form.querySelector('[type="submit"]');
    if (!(button instanceof HTMLElement) || button.classList.contains('is-loading')) return;
    // Light ring on a dark button, dark ring on a light or transparent one.
    const rgb = (getComputedStyle(button).backgroundColor.match(/[\d.]+/g) || []).map(Number);
    const dark = rgb.length >= 3 && (rgb[3] ?? 1) > 0.5 && 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2] < 128;
    button.classList.add('is-loading', 'novo-form-busy');
    button.setAttribute('aria-busy', 'true');
    button.insertAdjacentHTML('beforeend', `<span class="novo-busy${dark ? ' novo-busy--inverse' : ''}" aria-hidden="true"></span>`);
    // If a captcha step keeps the visitor on this page, the button comes back instead of spinning forever.
    setTimeout(() => novoClearBusy(button), 8000);
  },
  true
);

/* Back from the checkout or a posted form (bfcache): the button must not keep spinning. */
window.addEventListener('pageshow', () => {
  document.querySelectorAll('.cart__checkout-button.is-loading, .novo-form-busy.is-loading').forEach(novoClearBusy);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncHeaderMenuStyle);
} else {
  syncHeaderMenuStyle();
}
